import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoginForm } from '../components/auth/login/LoginForm';
import { PageBackground } from '../components/common/PageBackground';
import { ThemeToggle } from '../components/ThemeToggle';
import { HelpCircle } from 'lucide-react';
import { WelcomeForm, WelcomeFormData } from '../components/auth/onboarding/WelcomeForm';
import { ToastProvider, useToast } from '../components/common/Toast';
import { useResponsive } from '../contexts/ResponsiveContext';
import { useAnalyticsContext } from '../contexts/AnalyticsContext';
import apiClient from '../services/apiClient';
import { useAuthStore } from '../store';
import { User } from '../services/authService';
import { parseCustomInterviewParams, storePendingCustomInterview } from '../utils/customInterviewStorage';

interface LoginPageProps {
  onAuthComplete?: (userData?: { name: string }) => Promise<void>;
  onThemeToggle: (isDark: boolean) => void;
  theme: string;
}

// User data interface
interface UserData {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
  newUser: boolean;
}

// Page states
type PageState = 'login' | 'welcome' | 'complete';

function LoginPageContent({ onAuthComplete, onThemeToggle, theme }: LoginPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [animationPhase, setAnimationPhase] = useState(0); // 0: initial, 1: background, 2: container, 3: content
  const [pageState, setPageState] = useState<PageState>('login');
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { isMobile } = useResponsive();
  const { showToast } = useToast();
  const { trackEvent, trackSection } = useAnalyticsContext();
  const { setSSOUser } = useAuthStore();
  
  // Track page view on component mount
  useEffect(() => {
    // Set document title
    document.title = 'Welcome To MockTrail | Sign Up to get started';
    
    if (trackSection) {
      trackSection('Login Page');
    }
  }, [trackSection]);
  
  // Start sequence animations
  useEffect(() => {
    // Start the animation sequence
    setTimeout(() => setAnimationPhase(1), 300); // Background
    setTimeout(() => setAnimationPhase(2), 800); // Container
    setTimeout(() => setAnimationPhase(3), 1200); // Content
  }, []);
  
  // Check for custom interview creation URL parameters and store them
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const customInterviewParams = parseCustomInterviewParams(urlParams);
    
    if (customInterviewParams) {
      console.log('🔗 Custom interview creation detected on login page');
      console.log('📝 Storing custom interview params for after authentication:', customInterviewParams);
      
      // Store the parameters for after authentication
      storePendingCustomInterview(customInterviewParams);
      
      // Track the event
      if (trackEvent) {
        trackEvent('custom_interview_params_stored', {
          hasJobRole: !!customInterviewParams.jobRole,
          hasCompany: !!customInterviewParams.companyName,
          hasRoundType: !!customInterviewParams.interviewRoundType,
          hasJobDescription: !!customInterviewParams.jobDescription,
          source: 'login_page'
        });
      }
      
      // Clean up URL by removing the custom interview params
      const cleanUrl = location.pathname;
      navigate(cleanUrl, { replace: true });
    }
  }, [location.search, navigate, trackEvent, location.pathname]);
  
  // Check if accessed through /signup route and has referral code
  useEffect(() => {
    // Check if route is /signup
    const isSignupRoute = location.pathname === '/signup' || location.pathname === '/sign-up';
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    const authCode = urlParams.get('code');
    const source = urlParams.get('source');
    const state = urlParams.get('state');

    // ENHANCED: Handle direct JobProt signup with authorization code
    if (authCode && source === 'jobprot') {
      console.log('LoginPage: Direct JobProt signup URL detected');
      console.log('Auth code present:', !!authCode);
      console.log('Referral code present:', !!refCode);
      console.log('State parameter present:', !!state);
      
      // Track direct signup page visit
      if (trackEvent) {
        trackEvent('direct_signup_page_visited', {
          referral_code: refCode,
          auth_code_present: !!authCode,
          state_present: !!state,
          source: source,
          path: location.pathname
        });
      }
      
      // Store referral code if present for the LoginForm to use
      if (refCode) {
        localStorage.setItem('referralCode', refCode);
      }
      
      // The LoginForm will automatically handle the token exchange
      return;
    }

    // EXISTING: Handle regular signup routes
    if (isSignupRoute) {
      console.log('User accessed the app through /signup route');
      
      if (refCode) {
        console.log('Signup with referral code:', refCode);
        localStorage.setItem('referralCode', refCode);
        
        // Track signup route with referral
        if (trackEvent) {
          trackEvent('signup_page_visited', {
            referral_code: refCode,
            path: location.pathname
          });
        }
      } else {
        // Track signup route without referral
        if (trackEvent) {
          trackEvent('signup_page_visited', {
            referral_code: null,
            path: location.pathname
          });
        }
      }
    } else {
      // Track login page visit
      if (trackEvent) {
        trackEvent('login_page_visited', {
          path: location.pathname,
          referrer: document.referrer || 'direct'
        });
      }
    }
  }, [location, trackEvent]);
  
  // Add effect to disable body scrolling
  useEffect(() => {
    // Disable body scrolling on both mobile and desktop
    document.body.style.overflow = 'hidden';
    
    // Clean up on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  
  // Listen for custom event to show welcome form
  useEffect(() => {
    // Create stable handler to prevent duplicate executions
    const handleShowWelcomeForm = () => {
      // Only proceed if we're not already showing the welcome form
      if (pageState === 'welcome') {
        console.log('LoginPage: Welcome form already showing, skipping event');
        return;
      }

      console.log('LoginPage: Received showWelcomeForm event');
      
      // Get user data from authStore
      const authUser = useAuthStore.getState().user;
      
      if (authUser) {
        // Create user data object for the welcome form
        const fullUserData: UserData = {
          id: authUser._id || 'mock-id',
          name: authUser.name || 'User',
          email: authUser.email || '',
          profilePicture: authUser.profilePicture,
          newUser: true
        };
        
        // Set user data in state
        setUserData(fullUserData);
        
        console.log("LoginPage - handleShowWelcomeForm: Setting showing_welcome_form flag to true");
        // Set welcome form flag in local storage to maintain state across refreshes
        localStorage.setItem('showing_welcome_form', 'true');
        
        // Change to welcome page state
        setPageState('welcome');
        
        console.log('LoginPage: Showing welcome form from custom event');
      } else {
        console.warn('LoginPage: Cannot show welcome form, no user data available');
      }
    };
    
    // Add event listener
    window.addEventListener('showWelcomeForm', handleShowWelcomeForm);
      
    // Check if we should show the welcome form on mount and we have a valid user
    if (localStorage.getItem('showing_welcome_form') === 'true' && pageState !== 'welcome') {
      // Small delay to ensure user data is loaded
      setTimeout(() => {
        const authUser = useAuthStore.getState().user;
        if (authUser) {
          console.log('LoginPage: Auto-showing welcome form on mount');
          handleShowWelcomeForm();
        } else {
          console.log('LoginPage: User data not available yet, will wait for event');
        }
      }, 100);
    }
    
    // Clean up
    return () => {
      window.removeEventListener('showWelcomeForm', handleShowWelcomeForm);
    };
  }, [pageState]);
  
  // Handle successful login from email/password or SSO
  const handleLoginSuccess = async (user?: { name: string; newUser?: boolean; id?: string; email?: string }) => {
    if (!user) return;
    
    // Prevent multiple redirects
    if (isRedirecting) return;
    setIsRedirecting(true);
    
    try {
      // Track login success
      trackEvent?.('login_success', { 
        method: 'internal',
        new_user: !!user.newUser
      });
      
      // Set isAuthenticated flag in localStorage for auth state persistence
      localStorage.setItem('isAuthenticated', 'true');
      
      // Create full user data
      const fullUserData: UserData = {
        id: user.id || 'mock-id',
        name: user.name,
        email: user.email || `${user.name.toLowerCase().replace(/\s+/g, '.')}@example.com`,
        newUser: user.newUser || false
      };
      
      // Set user data in state
      setUserData(fullUserData);
      
      // If new user, show welcome form, otherwise redirect to dashboard
      if (fullUserData.newUser) {
        // Show success toast
        if (showToast) {
          showToast('Successfully logged in! Please complete your profile.', 'success');
        }
        console.log("LoginPage - handleLoginSuccess:  Setting showing_welcome_form flag to true");
        // Set a flag to indicate we're showing the welcome form
        // This prevents App.tsx from redirecting away from the login page
        localStorage.setItem('showing_welcome_form', 'true');
        
        // Change to welcome page state
        setPageState('welcome');
        
        console.log("New user detected - showing welcome form");
      } else {
        // Existing user, navigate to dashboard directly
        if (showToast) {
          showToast('Welcome back! Redirecting to your dashboard...', 'success');
        }
        
        // Complete the login flow
        if (onAuthComplete) {
          await onAuthComplete({ name: fullUserData.name });
        } else {
          // If no onLogin provided, redirect manually after a short delay
          setTimeout(() => {
            navigate('/dashboard');
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error during login flow:', error);
      if (showToast) {
        showToast('An error occurred during login. Please try again.', 'error');
      }
      
      // Track error
      trackEvent?.('login_flow_error', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsRedirecting(false);
    }
  };
  
  // Handle SSO login
  const handleSSOLogin = async (response: any) => {
    console.log("SSO login response:", response);
    
    // Check for different response structures - normalized or direct
    // Look for user data and tokens in multiple possible locations
    const user = response.user || response.data?.user;
    const access_token = response.access_token || response.data?.access_token || response.token || response.data?.token;
    const refresh_token = response.refresh_token || response.data?.refresh_token;
    
    // Validate we have the required data
    if (!user) {
      console.error("Missing user data in SSO response:", response);
      if (showToast) {
        showToast('Authentication failed: Missing user data', 'error');
      }
      trackEvent?.('login_failure', { 
        method: 'sso',
        provider: 'jobprot',
        stage: 'data_validation',
        reason: "Missing user data in response"
      });
      return;
    }
    
    if (!access_token) {
      console.error("Missing access token in SSO response:", response);
      if (showToast) {
        showToast('Authentication failed: Missing access token', 'error');
      }
      trackEvent?.('login_failure', { 
        method: 'sso',
        provider: 'jobprot',
        stage: 'data_validation',
        reason: "Missing access token in response"
      });
      return;
    }
    
    // Store tokens in localStorage for persistence
    localStorage.setItem('mocktrail-token', access_token);
    
    // Store refresh token if available
    if (refresh_token) {
      localStorage.setItem('mocktrail-refresh-token', refresh_token);
    }
    
    // Also update the apiClient with the token for immediate use
    apiClient.setToken(access_token);
    
    // Set isAuthenticated flag in localStorage for auth state persistence
    localStorage.setItem('isAuthenticated', 'true');
    
    // Track token received
    trackEvent?.('auth_tokens_received', {
      auth_method: 'sso',
      provider: 'jobprot'
    });
    
    // Update the auth store directly to ensure proper authentication state
    setSSOUser(user as User);
    
    // Handle user based on whether they're new or existing
    await handleLoginSuccess({
      id: user._id,
      name: user.name,
      email: user.email,
      newUser: user.newUser
    });
  };
  
  // Handle form completion
  const handleWelcomeFormComplete = async (formData: WelcomeFormData) => {
    // Prevent multiple submissions
    if (isRedirecting) return;
    setIsRedirecting(true);
    
    try {
      // Track welcome form completion
      trackEvent?.('welcome_form_complete', {
        preferences: Object.keys(formData).length
      });
      
      // In a real app, you would save the user preferences to a server here
      console.log('User preferences:', formData);
      
      // Show success toast for form submission
      if (showToast) {
        showToast('Thank you for answering the questions!', 'success');
      }
      
      // Move to the completed state
      setPageState('complete');
      
      // Clear the welcome form flag
      localStorage.removeItem('showing_welcome_form');
      
      // For new users, we want to redirect to setup-profile page
      // since the survey API is already called from the WelcomeForm component
      if (userData?.newUser) {
        console.log('New user completed welcome form, redirecting to setup-profile');
        navigate('/setup-profile', { replace: true });
        return;
      }
      
      // Only call onLogin if we're not navigating (for backwards compatibility)
      if (onAuthComplete && userData) {
        await onAuthComplete({ name: userData.name });
      } else if (onAuthComplete) {
        await onAuthComplete({ name: '-' }); // Fallback if userData is null
      } else {
        // Navigate directly after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error) {
      console.error('Error during welcome form submission:', error);
      if (showToast) {
        showToast('An error occurred while saving your preferences. Please try again.', 'error');
      }
      
      // Track error
      trackEvent?.('welcome_form_error', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsRedirecting(false);
    }
  };
  
  // Handle welcome form skip
  const handleWelcomeFormSkip = () => {
    // Prevent multiple skips
    if (isRedirecting) return;
    setIsRedirecting(true);
    
    try {
      // Track welcome form skip
      trackEvent?.('welcome_form_skip', {});
      
      if (showToast) {
        showToast('Preferences skipped', 'info');
      }
      
      setPageState('complete');
      
      // Clear the welcome form flag
      localStorage.removeItem('showing_welcome_form');
      
      // For new users, we want to redirect to setup-profile page
      if (userData?.newUser) {
        console.log('New user skipped welcome form, redirecting to setup-profile');
        navigate('/setup-profile', { replace: true });
        return;
      }
      
      // Only call onLogin if we're not navigating (for backwards compatibility)
      if (onAuthComplete && userData) {
        onAuthComplete({ name: userData.name });
      } else if (onAuthComplete) {
        onAuthComplete({ name: '-' }); // Fallback if userData is null
      } else {
        // Navigate directly after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (error) {
      console.error('Error during welcome form skip:', error);
      // Track error
      trackEvent?.('welcome_form_skip_error', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsRedirecting(false);
    }
  };
  
  const handleSupportClick = () => {
    // Track support action
    trackEvent?.('support_clicked', {
      page: 'login',
      context: 'header'
    });
    
    window.open('mailto:support@mocktrail.app', '_blank');
  };

  const handleThemeToggle = (isDark: boolean) => {
    // Track theme toggle action
    trackEvent?.('theme_changed', {
      new_theme: isDark ? 'dark' : 'light',
      page: 'login'
    });
    
    if (onThemeToggle) onThemeToggle(isDark);
  };
  
  return (
    <>
      <PageBackground />

      <div className="flex flex-col items-center justify-center overflow-hidden w-full">
        {/* Theme Toggle & Support */}
        <div className="fixed top-6 right-6 z-[100] flex items-center justify-center md:justify-end gap-3 transition-all duration-500 ease-out w-auto" 
            style={{ 
              opacity: animationPhase >= 2 ? 1 : 0, 
              transform: animationPhase >= 2 ? 'translateY(0)' : 'translateY(-1rem)' 
            }}>
          <button 
            onClick={handleSupportClick}
            className="p-2 rounded-full bg-surface-light/90 dark:bg-surface-dark/90 backdrop-blur-md border border-border-light dark:border-border-dark text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors duration-300 shadow-sm"
            aria-label="Support"
            title="Support"
          >
            <HelpCircle size={20} />
          </button>
          <ThemeToggle onToggle={handleThemeToggle} theme={theme} />
        </div>
        
        {/* Login Page Container - no scrolling */}
        <div className="min-h-screen w-full overflow-hidden flex items-center justify-center">
          {/* Floating particles with reduced opacity */}
          <div className={`
            absolute left-[10%] top-[15%] w-6 h-6 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float-slow
            transition-opacity duration-1000 ease-out
            ${animationPhase >= 1 ? 'opacity-100' : 'opacity-0'}
          `} style={{ animationDelay: '0s', animationDuration: '7s' }}></div>
          <div className={`
            absolute left-[25%] top-[40%] w-4 h-4 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float-slow
            transition-opacity duration-1000 ease-out
            ${animationPhase >= 1 ? 'opacity-100' : 'opacity-0'}
          `} style={{ animationDelay: '1.5s', animationDuration: '5s' }}></div>
          <div className={`
            absolute right-[15%] top-[20%] w-5 h-5 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float-slow
            transition-opacity duration-1000 ease-out
            ${animationPhase >= 1 ? 'opacity-100' : 'opacity-0'}
          `} style={{ animationDelay: '0.7s', animationDuration: '8s' }}></div>
          <div className={`
            absolute right-[35%] top-[65%] w-3 h-3 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float-slow
            transition-opacity duration-1000 ease-out
            ${animationPhase >= 1 ? 'opacity-100' : 'opacity-0'}
          `} style={{ animationDelay: '2s', animationDuration: '6s' }}></div>
          
          {/* Diagonal subtle accent lines */}
          <div className={`
            hidden md:block absolute -left-20 top-40 w-[500px] h-[1px] bg-gradient-to-r from-transparent via-primary-light/15 to-transparent rotate-[30deg]
            transition-opacity duration-1000 ease-out
            ${animationPhase >= 1 ? 'opacity-100' : 'opacity-0'}
          `}></div>
          <div className={`
            hidden md:block absolute -right-20 top-96 w-[500px] h-[1px] bg-gradient-to-r from-transparent via-secondary-light/15 to-transparent -rotate-[30deg]
            transition-opacity duration-1000 ease-out
            ${animationPhase >= 1 ? 'opacity-100' : 'opacity-0'}
          `}></div>
          
          {/* Subtle gradient background to highlight section borders */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-light/5 to-secondary-light/5 dark:from-primary-dark/5 dark:to-secondary-dark/5 opacity-40"></div>
          
          {/* Main container - adjusted to take only needed height on mobile */}
          <div className={`
            w-[calc(100%-2rem)] ${isMobile ? 'h-auto max-h-[85vh] mt-6' : 'max-w-7xl max-h-[90vh]'} bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-md rounded-2xl overflow-hidden shadow-sm border border-border-light dark:border-border-dark flex flex-col md:flex-row
            transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]
            ${animationPhase >= 2 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
          `}>
            {/* Left Side: SlideShow - Hidden on mobile, visible on desktop */}
            <div className={`
              w-0 md:w-[60%] h-0 md:h-full overflow-hidden md:overflow-visible
              transition-all duration-700 ease-out
              ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}
            `} style={{ transitionDelay: '200ms' }}>
              <div className="h-full w-full p-2 flex">
                {/* <SlideShowCard slides={slides} isDarkMode={theme === 'dark'} /> */}
                <div className="w-full h-full flex flex-col items-center justify-center py-12">
                  <h2 className={`px-20 text-3xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark transform transition-all duration-500 ${
                    animationPhase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}>
                    Get Spotted - No More Practicing in Shadow
                  </h2>
                  <img 
                    src= {theme === 'dark' ? "/mocktrail-getspottedimg-darkui.png" : "/mocktrail-getspottedimg-lightui.png"} 
                    alt="MockTrail x JobProt" 
                    className="mt-12 w-full h-full hover:scale-105 transition-transform duration-300"
                    style={{
                      opacity: animationPhase >= 3 ? 1 : 0,
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* Right Side Content: Login Form or Welcome Form based on state */}
            <div className={`
              w-full md:w-1/2 h-auto flex justify-center items-center
              transition-all duration-700 ease-out
              ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}
            `} style={{ transitionDelay: '400ms' }}>
              {pageState === 'login' && (
                <LoginForm
                  onSSOSuccess={handleSSOLogin}
                />
              )}
              
              {pageState === 'welcome' && userData && (
                <WelcomeForm 
                  userName={userData.name}
                  email={userData.email}
                  onComplete={handleWelcomeFormComplete}
                  onSkip={handleWelcomeFormSkip}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Wrap the main component with the ToastProvider for notifications
export function LoginPage({ onAuthComplete, onThemeToggle, theme }: LoginPageProps) {
  return (
    <ToastProvider>
      <LoginPageContent onAuthComplete={onAuthComplete} onThemeToggle={onThemeToggle} theme={theme} />
    </ToastProvider>
  );
}